import { endOfCalendarDayBrazilMs } from './brazilTime';
import type { Cycle } from '../types';

export { endOfCalendarDayBrazilMs };

/** Formata data de ciclo (só calendário) em pt-BR sem deslocar o dia por fuso local. */
export function formatCycleDateBr(iso: string | undefined | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

/** Valor `YYYY-MM-DD` para `<input type="date">` a partir do ISO da API. */
export function cycleDateToInputValue(iso: string | undefined | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Após o fim do dia (Brasília) do prazo de envio (`deadlineDate`), não se aceitam novos envios.
 * Por padrão o prazo é 3 dias corridos após o fim do período do ciclo.
 */
export function isCycleClosedForSubmissions(deadlineDateIso: string): boolean {
  const end = endOfCalendarDayBrazilMs(deadlineDateIso);
  if (Number.isNaN(end)) return false;
  return Date.now() > end;
}

/** Soma dias civis a uma data `YYYY-MM-DD` (usada no formulário de ciclo). */
export function addCalendarDaysYmd(ymd: string, days: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd.trim());
  if (!m) return ymd;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days, 12, 0, 0, 0));
  if (Number.isNaN(d.getTime())) return ymd;
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

/** Sugestão padrão de prazo de envio: 3 dias corridos após a data de fim do período. */
export function defaultDeadlineFromEndDate(endDateYmd: string): string {
  if (!endDateYmd) return '';
  return addCalendarDaysYmd(endDateYmd, 3);
}

/** Últimos 7 dias até o fim do dia do prazo (deadline, calendário UTC), inclusive após o prazo. */
export function isInDeadlineUrgencyWindow(deadlineIso: string): boolean {
  const d = new Date(deadlineIso);
  if (Number.isNaN(d.getTime())) return false;
  const y = d.getUTCFullYear();
  const mo = d.getUTCMonth();
  const date = d.getUTCDate();
  const weekStart = Date.UTC(y, mo, date - 6, 0, 0, 0, 0);
  return Date.now() >= weekStart;
}

/** Linha em alerta: falta receber do gestor e/ou enviar reverso, na janela de urgência. */
export function shouldHighlightTeamFeedbackRow(
  deadlineIso: string,
  receivedFromManagerAt: string | null | undefined,
  reverseSentAt: string | null | undefined,
): boolean {
  if (receivedFromManagerAt && reverseSentAt) return false;
  return isInDeadlineUrgencyWindow(deadlineIso);
}

export function formatDateTimeBr(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/** Ciclo em vigor (ativo e dentro do prazo de envio). */
export function isCycleCurrentlyOpen(cycle: Pick<Cycle, 'active' | 'deadlineDate'>): boolean {
  return cycle.active && !isCycleClosedForSubmissions(cycle.deadlineDate);
}

type CycleLike = Pick<Cycle, 'id' | 'name' | 'startDate' | 'endDate' | 'deadlineDate' | 'active'>;

/** Une ciclos da API com ciclos referenciados nos feedbacks (histórico completo). */
export function mergeCyclesForFilter(
  apiCycles: Cycle[],
  ...feedbackLists: Array<Array<{ cycleId: string; cycle?: Cycle | null }>>
): Cycle[] {
  const map = new Map<string, Cycle>();
  for (const c of apiCycles) {
    map.set(c.id, c);
  }
  for (const list of feedbackLists) {
    for (const item of list) {
      // Não sobrescreve o registro da API (evita `active` stale vindo do join do feedback).
      if (item.cycle?.id && !map.has(item.cycle.id)) {
        map.set(item.cycle.id, item.cycle);
      }
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
  );
}

/** Ciclo em vigor na plataforma (ativo e dentro do prazo de envio). */
export function findCurrentActiveCycle(cycles: CycleLike[]): CycleLike | undefined {
  const open = cycles.filter(isCycleCurrentlyOpen);
  if (open.length === 0) return undefined;
  return open.reduce((best, c) =>
    new Date(c.startDate).getTime() >= new Date(best.startDate).getTime() ? c : best,
  );
}

/** Opções do filtro por ciclo (ciclo atual primeiro, depois encerrados — mais recentes no topo). */
export function buildCycleFilterSelectOptions(cycles: CycleLike[]): Array<{ value: string; label: string }> {
  const current = findCurrentActiveCycle(cycles);
  const closed = cycles.filter((c) => c.id !== current?.id);

  const toOption = (cycle: CycleLike, isCurrent: boolean) => {
    const periodEnd = formatCycleDateBr(cycle.endDate);
    const status = isCurrent ? 'Ciclo atual' : 'Encerrado';
    return {
      value: cycle.id,
      label: `${cycle.name} · ${status} (${periodEnd})`,
    };
  };

  const options: Array<{ value: string; label: string }> = [];
  if (current) {
    options.push(toOption(current, true));
  }
  options.push(...closed.map((c) => toOption(c, false)));
  return options;
}
